import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function Signup() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [form, setForm] = useState({
    email: "",
    password: "",
    username: "",
    displayName: "",
    inviteCode: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const { email, password, username, displayName, inviteCode } = form;
    if (!email || !password || !username || !displayName || !inviteCode) {
      toast("all fields required");
      return;
    }
    if (password.length < 8) {
      toast("password must be 8+ characters");
      return;
    }
    if (!/^[a-zA-Z0-9._-]+$/.test(username)) {
      toast("username: letters, numbers, . _ - only");
      return;
    }
    setSubmitting(true);
    try {
      await signup({
        email: email.trim(),
        password,
        username: username.trim().toLowerCase(),
        displayName: displayName.trim(),
        city: "toronto",
        inviteCode: inviteCode.trim(),
      });
      toast("welcome.");
      navigate("/home", { replace: true });
    } catch (err) {
      toast(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="signup-page"
      className="min-h-screen bg-black text-white flex flex-col"
    >
      <header className="px-5 pt-6 pb-4 flex items-center justify-between">
        <Link
          to="/"
          className="font-mono text-sm tracking-[0.2em] uppercase text-white hover:text-acid"
        >
          <span className="text-acid">/</span> the lobby
        </Link>
        <Link
          to="/login"
          data-testid="signup-to-login"
          className="font-mono text-[11px] uppercase tracking-wider text-white/60 hover:text-acid"
        >
          have an account?
        </Link>
      </header>

      <section className="flex-1 px-5 pt-10 pb-16">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-acid mb-4">
          ↓ alpha · invite-only
        </div>
        <h1 className="font-display text-5xl sm:text-6xl leading-[0.9] tracking-tight">
          sign<br />up<span className="text-acid">.</span>
        </h1>
        <p className="mt-4 font-mono text-[11px] text-white/50 leading-relaxed">
          you need an invite code. ask the person who told you about this.
        </p>

        <form onSubmit={submit} data-testid="signup-form" className="mt-8 space-y-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
              invite code
            </label>
            <input
              data-testid="signup-invite"
              value={form.inviteCode}
              onChange={set("inviteCode")}
              placeholder="lobby-alpha-..."
              className="mt-1 w-full bg-transparent border-b-2 border-acid/60 px-1 py-2.5 font-mono text-base placeholder:text-white/20 focus:border-acid outline-none"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
              email
            </label>
            <input
              data-testid="signup-email"
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="you@theatre.com"
              className="mt-1 w-full bg-transparent border-b-2 border-white/30 px-1 py-2.5 font-mono text-base placeholder:text-white/20 focus:border-acid outline-none"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
              password (8+ chars)
            </label>
            <input
              data-testid="signup-password"
              type="password"
              value={form.password}
              onChange={set("password")}
              placeholder="••••••••"
              className="mt-1 w-full bg-transparent border-b-2 border-white/30 px-1 py-2.5 font-mono text-base placeholder:text-white/20 focus:border-acid outline-none"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
              username
            </label>
            <input
              data-testid="signup-username"
              value={form.username}
              onChange={set("username")}
              placeholder="ushers.usher"
              className="mt-1 w-full bg-transparent border-b-2 border-white/30 px-1 py-2.5 font-mono text-base placeholder:text-white/20 focus:border-acid outline-none"
              autoCapitalize="off"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
              display name
            </label>
            <input
              data-testid="signup-displayname"
              value={form.displayName}
              onChange={set("displayName")}
              placeholder="how you want to show up"
              className="mt-1 w-full bg-transparent border-b-2 border-white/30 px-1 py-2.5 font-mono text-base placeholder:text-white/20 focus:border-acid outline-none"
            />
          </div>

          <button
            type="submit"
            data-testid="signup-submit"
            disabled={submitting}
            className="w-full mt-4 bg-acid text-black font-mono text-sm uppercase tracking-wider py-4 border-2 border-acid hover:bg-black hover:text-acid transition-colors disabled:opacity-50"
          >
            {submitting ? "creating..." : "create account"}
          </button>
        </form>

        <div className="mt-8 font-mono text-[11px] text-white/50">
          already in?{" "}
          <Link to="/login" className="text-acid underline underline-offset-4">
            log in
          </Link>
        </div>
      </section>
    </main>
  );
}
